import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Award, Users, Target, Heart, ArrowRight, Palette, Ruler } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Helmet } from "react-helmet-async";

<Helmet>
  <title>About Us | Trusted Uniform Manufacturer in India</title>
  <meta name="description" content="We are a leading uniform supplier offering premium hotel, hospital, and school uniforms with modern designs, durable stitching, and customized solutions." />
  <meta name="keywords" content="about uniform company, uniform manufacturer India, custom uniform supplier, quality uniforms" />
  <meta property="og:title" content="Premium Uniform Manufacturer in India" />
  <meta property="og:description" content="High-quality uniforms for hotels, hospitals, and schools." />
  <meta property="og:type" content="website" />

</Helmet>

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_4a46bb01-e425-48c8-a790-c185a7fd0b57/artifacts/6wdpibd5_logomedusa.jpeg';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export const About = () => {
  const { isDarkMode } = useTheme();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={`pt-24 md:pt-28 ${isDarkMode ? 'bg-zinc-900' : ''}`} data-testid="about-page">
      {/* Hero Section with Logo Background */}
      <section className={`py-16 md:py-24 relative overflow-hidden ${isDarkMode ? 'bg-zinc-800' : 'bg-[#fdfbf5]'}`}>
        {/* Logo Background */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src="/public/logo1.jpeg"
            alt="Malabisthestic"
            className="w-full h-full object-contain opacity-[0.06]"
            style={{
              filter: isDarkMode ? 'invert(1) sepia(0.5)' : 'sepia(0.3)',
              maxWidth: '50%',
              maxHeight: '70%'
            }}
          />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial="initial"
            animate="animate"
            variants={fadeUp}
          >
            <p className={`section-subtitle ${isDarkMode ? 'text-gray-400' : ''}`}>Our Story</p>
            <h1
              className={`text-3xl sm:text-4xl lg:text-5xl mb-6 ${isDarkMode ? 'text-white' : ''}`}
              style={{ fontFamily: 'Bodoni Moda, serif' }}
            >
              About MALABIStheistic
            </h1>
            <div className="gold-line mx-auto mb-8" />
            <p className={`text-lg leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Where elegance meets uniformity. We design premium uniforms that empower
              hospitality and healthcare professionals to look and feel their best.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Brand Story */}
      <section className={`py-20 md:py-32 ${isDarkMode ? 'bg-zinc-900' : 'bg-white'}`}>
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <div className="relative">
                <img
                  src="/about.png"
                  alt="Malabisthestic Dehradun"
                  className="w-full aspect-[4/5] object-cover"
                />
                {/* Overlay Logo */}
                <div className={`absolute bottom-8 right-8 p-4 shadow-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-white'}`}>
                  <img
                    src="/logo.jpeg"
                    alt="MALABISthestic"
                    className="w-16 h-16 object-contain"
                    style={{ filter: isDarkMode ? 'invert(1) sepia(0.3)' : 'sepia(0.2)' }}
                  />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <p className={`section-subtitle ${isDarkMode ? 'text-gray-400' : ''}`}>The Beginning</p>
              <h2 className={`section-title mb-6 ${isDarkMode ? 'text-white' : ''}`}>Our Journey</h2>
              <div className="gold-line mb-8" />

              <div className={`space-y-6 leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                We are dedicated to creating high-quality, professional uniforms for hotels, hospitals, and educational institutions. Our mission is simple — to deliver uniforms that combine comfort, durability, and style while reflecting the identity and standards of every organization we work with.

                With a deep understanding of industry needs, we design uniforms that not only look professional but also support daily performance. From front office elegance in hotels to hygienic and functional hospital wear, and smart, comfortable school uniforms — every product is crafted with attention to detail and premium materials.

                We believe that uniforms are more than just clothing — they represent discipline, trust, and brand image. That’s why we focus on quality craftsmanship, customization, and timely delivery to ensure complete customer satisfaction.

                Our goal is to build long-term relationships by providing reliable service and uniforms that truly make a difference.
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section - Icons visible in both modes */}
      <section className={`py-20 md:py-32 ${isDarkMode ? 'bg-zinc-800' : 'bg-[#0a0a0a]'} text-white`}>
        <div className="container-custom">
          <motion.div
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <p className="section-subtitle text-gray-400">What Drives Us</p>
            <h2 className="section-title text-white">Our Core Values</h2>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {[
              {
                icon: <Award className="w-8 h-8 text-[#D4AF37]" />,
                title: 'Excellence',
                description: 'Unwavering commitment to design excellence in every piece.'
              },
              {
                icon: <Users className="w-8 h-8 text-[#D4AF37]" />,
                title: 'Partnership',
                description: 'Building lasting relationships with our clients and their teams.'
              },
              {
                icon: <Target className="w-8 h-8 text-[#D4AF37]" />,
                title: 'Precision',
                description: 'Meticulous attention to detail in design and execution.'
              },
              {
                icon: <Heart className="w-8 h-8 text-[#D4AF37]" />,
                title: 'Passion',
                description: 'Love for what we do reflects in every design we create.'
              }
            ].map((value, index) => (
              <motion.div
                key={value.title}
                className="text-center p-8 border border-gray-700 hover:border-[#D4AF37] transition-colors"
                variants={fadeUp}
              >
                <div className="mb-4 flex justify-center">
                  {value.icon}
                </div>
                <h3
                  className="text-lg mb-3 text-white"
                  style={{ fontFamily: 'Bodoni Moda, serif' }}
                >
                  {value.title}
                </h3>
                <p className="text-sm text-gray-400">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* What We Do */}
      <section className={`py-20 md:py-32 ${isDarkMode ? 'bg-zinc-900' : 'bg-white'}`}>
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <motion.div
              className={`p-8 md:p-12 ${isDarkMode ? 'bg-zinc-800' : 'bg-[#fdfbf5]'}`}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <div className="flex items-center gap-4 mb-6">
                <Ruler className={`w-8 h-8 ${isDarkMode ? 'text-[#D4AF37]' : 'text-[#D4AF37]'}`} />
                <h2
                  className={`text-2xl md:text-3xl ${isDarkMode ? 'text-white' : ''}`}
                  style={{ fontFamily: 'Bodoni Moda, serif' }}
                >
                  As Per Your Measurement
                </h2>
              </div>
              <div className="gold-line mb-6" />
              <p className={`leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                We deal in customization only. Every design is tailored to your exact measurements.
                Share your measurements with us and we'll create the perfect fit for your team.
                No standard sizes - every piece is made specifically for you.
              </p>
            </motion.div>

            <motion.div
              className={`p-8 md:p-12 text-white ${isDarkMode ? 'bg-zinc-800 border border-zinc-700' : 'bg-[#0a0a0a]'}`}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <div className="flex items-center gap-4 mb-6">
                <Palette className="w-8 h-8 text-[#D4AF37]" />
                <h2
                  className="text-2xl md:text-3xl"
                  style={{ fontFamily: 'Bodoni Moda, serif' }}
                >
                  As Per Your Colour
                </h2>
              </div>
              <div className="gold-line mb-6" />
              <p className="text-gray-300 leading-relaxed">
                Select your preferred colours for any design. We customize the colour combination
                based on your brand identity and preferences. Your chosen colours, your unique look.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className={`py-20 md:py-32 ${isDarkMode ? 'bg-zinc-800' : 'bg-[#f9fafb]'}`}>
        <div className="container-custom">
          <motion.div
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <p className={`section-subtitle ${isDarkMode ? 'text-gray-400' : ''}`}>The MALABIStheistic Advantage</p>
            <h2 className={`section-title ${isDarkMode ? 'text-white' : ''}`}>Why Partner With Us</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                number: '01',
                title: 'Design Selection',
                description: 'Choose from our wide range of premium uniform designs.'
              },
              {
                number: '02',
                title: 'Custom Measurements',
                description: 'Every uniform is tailored to your exact measurements.'
              },
              {
                number: '03',
                title: 'Colour Customization',
                description: 'Select your preferred colours for any design.'
              },
              {
                number: '04',
                title: 'Bulk Orders',
                description: 'Perfect for hotels, hospitals and large institutions.'
              },
              {
                number: '05',
                title: 'Variety of Styles',
                description: 'Multiple designs available for each uniform category.'
              },
              {
                number: '06',
                title: 'Dedicated Support',
                description: 'We guide you through the entire customization process.'
              }
            ].map((item, index) => (
              <motion.div
                key={item.number}
                className={`p-8 border transition-colors ${isDarkMode
                    ? 'bg-zinc-900 border-zinc-700 hover:border-[#D4AF37]'
                    : 'bg-white border-gray-100 hover:border-[#D4AF37]'
                  }`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <span
                  className={`text-4xl font-light mb-4 block ${isDarkMode ? 'text-zinc-700' : 'text-gray-200'}`}
                  style={{ fontFamily: 'Bodoni Moda, serif' }}
                >
                  {item.number}
                </span>
                <h3
                  className={`text-lg mb-3 ${isDarkMode ? 'text-white' : ''}`}
                  style={{ fontFamily: 'Bodoni Moda, serif' }}
                >
                  {item.title}
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-20 md:py-32 ${isDarkMode ? 'bg-zinc-900' : 'bg-white'}`}>
        <div className="container-custom">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <p className={`section-subtitle ${isDarkMode ? 'text-gray-400' : ''}`}>Let's Work Together</p>
            <h2 className={`section-title mb-8 ${isDarkMode ? 'text-white' : ''}`}>
              Ready to Get Started?
            </h2>
            <p className={`mb-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Join the growing list of prestigious hotels and healthcare institutions
              that trust MALABIStheistic for their uniform designs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/products" className="btn-primary inline-flex items-center gap-3" data-testid="about-view-products">
                View Our Designs
                <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className={`btn-secondary ${isDarkMode ? 'border-white text-white hover:bg-white hover:text-black' : ''}`} data-testid="about-contact">
                Get in Touch
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
